/**
 * Google Maps Component
 * Initializes Google Maps instances found in the DOM.
 * Uses global settings passed from app.js for API key and defaults.
 * Handles dynamic loading of the Google Maps API script.
 */

import { Loader } from '@googlemaps/js-api-loader';
import { debounce } from '../core/debounce.js';
import { isTouch } from '../core/is-touch.js';
import { getLenisInstance } from '../core/scroll-manager.js';

// --- Module-level Constants for easy configuration ---
const CONSTANTS = {
    Z_INDEX: {
        PARENT_MARKER: 100,
        CHILD_MARKER_DEFAULT: 50,
        CHILD_MARKER_ACTIVE: 101,
        RADIAL_BG: 1,
        RADIAL_CIRCLE: 2,
        RADIAL_LABEL: 3,
    },
    ANIMATION: {
        BOUNCE_DURATION: 1420, // ~2 bounces in milliseconds
        POLYLINE_INTERVAL: 30, // milliseconds for fade in/out
    },
    MARKER_LABEL: {
        LABEL_OFFSET: -1, // Vertical pixel offset for non-SVG labels. Negative moves it up.
        USE_LEADING_ZERO: true, // Set to true to format numbers as 01, 02, etc.
    },
    MAP: {
        MAX_FIT_BOUNDS_ZOOM: 17,
    },
    ICONS: {
        TRANSPARENT_1x1: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=',
    },
    ICON_SCALE: 3, // Scale factor for non-SVG icons (e.g. 0.5 for retina images)
};

/**
 * Checks if the current user agent is likely a bot or crawler.
 * @returns {boolean} True if the user agent matches a bot pattern, false otherwise.
 */
function isBot() {
    // A comprehensive list of common bot, crawler, and scraper user agents.
    // This helps prevent unnecessary Google Maps API loads from non-human traffic.
    const botPatterns = [
        /bot/i, /crawler/i, /spider/i, /crawling/i, /slurp/i, /seek/i,
        /google/i, /bing/i, /yandex/i, /duckduck/i, /msn/i,
        /baidu/i, /sogou/i, /exabot/i, /facebot/i, /petal/i,
        /ia_archiver/i, /archive\.org/i, /semrush/i, /ahrefs/i, /mj12/i,
        /dot/i, /nutch/i, /seznam/i, /grapeshot/i, /linkpad/i,
        /megaindex/i, /blex/i, /bytespider/i, /zoominfo/i, /voila/i,
        /psbot/i, /censysinspect/i, /serpstat/i, /barkrowler/i,
        /httpunit/i, /python-urllib/i, /libwww/i, /commons-httpclient/i,
        /java/i, /wget/i, /curl/i, /uptimerobot/i
    ];
    return botPatterns.some(pattern => pattern.test(navigator.userAgent));
}

// Export the main initialization function
export async function googleMaps(pageMapConfigs = {}, globalSettings, log = false) {
	// Check if a valid API key was passed
	if (!globalSettings?.apiKey) {
		if (log) console.warn('Google Maps: API key not provided. Cannot initialize maps.');
		return;
	}

	// Prevent bots and crawlers from loading the Google Maps API to save on costs.
	if (isBot()) {
		if (log) console.log('Google Maps: Bot detected. Skipping map initialization.');
		return;
	}

	// Find map elements in the DOM
	const maps = document.querySelectorAll('.google-map__canvas');
	if (!maps || maps.length === 0) {
		if (log) console.log('Google Maps: No map canvas elements found in the DOM.');
		return;
	}

	// Use default settings passed from app.js
	const defaultMapStyles = globalSettings.defaultStyles || []; // Local styles array
	const defaultMapMarker = globalSettings.defaultMarker || null; // Expects { url, width, height } or null

	// Configure the loader
	const mapsLoader = new Loader({
	  apiKey: globalSettings.apiKey,
	  version: "weekly",
	  libraries: ["places", "geometry", "marker"] // Added geometry for spherical calculations
	});

	try {
		// Load the Google Maps API
		const google = await mapsLoader.load();
		if (log) console.log('Google Maps: API loaded successfully.');

		// Custom OverlayView to replace AdvancedMarkerElement for HTML markers without Map ID
		class HTMLMapMarker extends google.maps.OverlayView {
			constructor(options) {
				super();
				this._position = options.position instanceof google.maps.LatLng ? options.position : new google.maps.LatLng(options.position);
				this._content = options.content;
				this._zIndex = options.zIndex || 1;
				this._map = options.map;
				
				// Properties used by the manager
				this.markerID = null;
				this.groupID = null;
				this.pathDisplay = null;
				this.dialog = null;
				this.originalSize = null;
				this.originalLabel = null;
				this.originalLabelFontSize = null;
				this.isSvgIconWithText = false;

				this.setMap(this._map);
			}

			onAdd() {
				const panes = this.getPanes();
				panes.overlayMouseTarget.appendChild(this._content);
				this._content.style.position = 'absolute';
				this._content.style.zIndex = this._zIndex;
				this._content.style.transform = 'translate(-50%, -100%)'; // Center bottom anchor
				this._content.style.cursor = 'pointer';
				// Prevent map drag when clicking marker
				['mousedown', 'touchstart', 'pointerdown', 'click'].forEach(evt => {
					this._content.addEventListener(evt, (e) => e.stopPropagation());
				});
			}

			draw() {
				const overlayProjection = this.getProjection();
				if (!overlayProjection || !this._content) return;
				const point = overlayProjection.fromLatLngToDivPixel(this._position);
				if (point) {
					this._content.style.left = point.x + 'px';
					this._content.style.top = point.y + 'px';
				}
			}

			onRemove() {
				if (this._content && this._content.parentElement) {
					this._content.parentElement.removeChild(this._content);
				}
			}

			get position() { return this._position; }
			set position(val) {
				this._position = val instanceof google.maps.LatLng ? val : new google.maps.LatLng(val);
				this.draw();
			}

			get content() { return this._content; }

			get zIndex() { return this._zIndex; }
			set zIndex(val) {
				this._zIndex = val;
				if (this._content) this._content.style.zIndex = val;
			}

			setZIndex(val) { this.zIndex = val; }
			getPosition() { return this._position; }
		}

		// Initialize each map found in the DOM
		for (const mapCanvasElement of maps) {
			const mapContainer = mapCanvasElement.closest('.google-map');
			if (!mapContainer || !mapContainer.id) {
				console.error('Google Maps: Found a canvas without a valid parent .google-map container or ID.');
				return;
			}

			const mapFullID = mapContainer.getAttribute("id"); // Use the full ID like 'google-map--v7sdgfso8'
			const mapShortID = mapFullID.replace('google-map--', ''); // e.g., 'v7sdgfso8'
			let mapSettings = pageMapConfigs[mapShortID] || {}; // Load specific settings from the passed config object.

			// --- Fallback/Default Settings if specific settings object not found ---
			if (Object.keys(mapSettings).length === 0) {
				if (log) console.log(`Google Maps: No specific settings object found for ${mapFullID}. Using data attributes.`);
				mapSettings = {
					centerMarker: {
						position: {
							lat: parseFloat(mapContainer.getAttribute('data-latitude')) || 0,
							lng: parseFloat(mapContainer.getAttribute('data-longitude')) || 0
						}
					},
					zoom: parseInt(mapContainer.getAttribute('data-zoom')) || 14,
					maxZoom: parseInt(mapContainer.getAttribute('data-max-zoom')) || null,
					minZoom: parseInt(mapContainer.getAttribute('data-min-zoom')) || null,
					primaryMarker: { // Assume primary marker is same as center if not specified
						position: {
							lat: parseFloat(mapContainer.getAttribute('data-latitude')) || 0,
							lng: parseFloat(mapContainer.getAttribute('data-longitude')) || 0
						},
						dialog: mapContainer.getAttribute('data-dialog') || '',
						icon: null // Will fallback to defaultMapMarker later
					},
					markers: [], // Assume no extra markers unless defined in specific settings
					directions: { enable_directions: false },
					radial: { enable: false },
					dialogType: 'dialog' // Default dialog type
				};
			}

			// Ensure required properties exist
			mapSettings.centerMarker = mapSettings.centerMarker || {};
			mapSettings.centerMarker.position = mapSettings.centerMarker.position || { lat: 0, lng: 0 };
			mapSettings.primaryMarker = mapSettings.primaryMarker || {};
			mapSettings.primaryMarker.position = mapSettings.primaryMarker.position || mapSettings.centerMarker.position;
			mapSettings.zoom = mapSettings.zoom || 14;
			mapSettings.markers = mapSettings.markers || [];
			mapSettings.directions = mapSettings.directions || { enable_directions: false };
			mapSettings.radial = mapSettings.radial || { enable: false };
			mapSettings.dialogType = mapSettings.dialogType || 'dialog'; // Default to 'dialog'

			const touch = isTouch();

			// --- Base Google Map Options ---
			let googleMapSettings = {
				zoom: mapSettings.zoom,
				maxZoom: mapSettings.maxZoom || null,
				minZoom: mapSettings.minZoom || null,
				center: { lat: mapSettings.centerMarker.position.lat, lng: mapSettings.centerMarker.position.lng },
				disableDefaultUI: false,
	            mapTypeControl: false,
	            rotateControl: false,
	            disableDoubleClickZoom: false,
	            draggable: true,
	            keyboardShortcuts: false,
	            scrollwheel: false,
	            streetViewControl: false,
	            zoomControl: true,
	            zoomControlOptions: {
	                position: google.maps.ControlPosition.RIGHT_CENTER
	            },
	            mapTypeId: google.maps.MapTypeId.ROADMAP,
	            styles: mapSettings.styles || defaultMapStyles, // Use specific styles or fallback to global default
				tilt: 0
			};

			// Adjust options for touch devices
			if (touch) {
				googleMapSettings.disableDefaultUI = true;
	            googleMapSettings.disableDoubleClickZoom = true;
	            googleMapSettings.draggable = false;
	            googleMapSettings.panControl = true; // Keep pan control maybe?
			}

			// --- Initialize Map ---
			let googleMap;
			try {
				googleMap = new google.maps.Map(mapCanvasElement, googleMapSettings);
				if (log) console.log(`Google Maps: Map initialized for ${mapFullID}`);
			} catch (error) {
				console.error(`Google Maps: Error initializing map for ${mapFullID}`, error);
				continue; // Stop processing this map if initialization failed
			}

			let bounds = new google.maps.LatLngBounds();

			// --- Directions Setup ---
			let directionsService = null;
			let directionsRenderer = null;
			if (mapSettings.directions.enable_directions === true) {
				directionsService = new google.maps.DirectionsService(); // Modernized: async/await usage below
				directionsRenderer = new google.maps.DirectionsRenderer({
					map: googleMap,
					suppressMarkers: true, // We handle markers manually
					polylineOptions: {
						strokeOpacity: 0, // Start invisible, animate in later
					}
				});
				if (log) console.log(`Google Maps: Directions service enabled for ${mapFullID}`);
			}

			// --- Primary Marker Setup ---
			let primaryMarkerIconOptions = null;
			// Use specific icon if provided, otherwise fallback to global default
			const specificPrimaryIcon = mapSettings.primaryMarker.icon;
			const iconToUse = specificPrimaryIcon || defaultMapMarker;

			if (iconToUse && iconToUse.url && iconToUse.width && iconToUse.height) {
				const baseWidth = parseInt(iconToUse.width);
				const baseHeight = parseInt(iconToUse.height);
				// If it's not an SVG, assume it's a retina image and halve its display size.
				const scaledWidth = iconToUse.svg ? baseWidth : baseWidth * CONSTANTS.ICON_SCALE;
				const scaledHeight = iconToUse.svg ? baseHeight : baseHeight * CONSTANTS.ICON_SCALE;

				primaryMarkerIconOptions = {
					url: iconToUse.url,
					scaledSize: new google.maps.Size(scaledWidth, scaledHeight),
					origin: new google.maps.Point(0, 0),
					anchor: new google.maps.Point(scaledWidth / 2, scaledHeight), // Center bottom anchor of the scaled image
				};
			}

			const parentMarkerEl = document.createElement('div');
			parentMarkerEl.className = 'marker parent-marker';

			if (primaryMarkerIconOptions && primaryMarkerIconOptions.url) {
				const img = document.createElement('img');
				img.src = primaryMarkerIconOptions.url;
				img.width = primaryMarkerIconOptions.scaledSize.width;
				img.height = primaryMarkerIconOptions.scaledSize.height;
				parentMarkerEl.appendChild(img);
			} else {
				// Fallback to default SVG marker if no icon is provided
				if (log) console.log(`Google Maps: No valid primary marker icon found for ${mapFullID}, using default SVG marker.`);
				parentMarkerEl.innerHTML = `<svg viewBox="0 0 27 43" width="27" height="43" style="filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));"><path fill="#EA4335" d="M13.5 0C6.055 0 0 6.055 0 13.5c0 10.125 13.5 29.5 13.5 29.5S27 23.625 27 13.5C27 6.055 20.945 0 13.5 0z"/><circle cx="13.5" cy="13.5" r="4.5" fill="#960A0A"/></svg>`;
			}

			// Create the primary/parent marker
			const parentMarker = new HTMLMapMarker({
				position: { lat: mapSettings.primaryMarker.position.lat, lng: mapSettings.primaryMarker.position.lng },
				map: googleMap,
				content: parentMarkerEl,
				zIndex: CONSTANTS.Z_INDEX.PARENT_MARKER // Ensure parent is usually above children
	        });

			parentMarker.markerID = 'parent';

			// Store the original size on the marker object for responsive scaling.
			// This should be the initial DISPLAYED size.
			if (iconToUse && iconToUse.width && iconToUse.height) {
				parentMarker.originalSize = {
					width: primaryMarkerIconOptions.scaledSize.width,
					height: primaryMarkerIconOptions.scaledSize.height
				};
			}
	        bounds.extend(parentMarker.position); // Include parent in bounds calculation

	        // Info window for parent marker (if dialog content exists)
	        let parentInfoWindow = null;
	        if (mapSettings.primaryMarker.dialog) {
				let pw = parentMarker.originalSize ? parentMarker.originalSize.width : 27;
				let ph = parentMarker.originalSize ? parentMarker.originalSize.height : 43;
		        parentInfoWindow = new google.maps.InfoWindow({
					content: `<div class="dialog-wrap">${htmlDecode(mapSettings.primaryMarker.dialog)}</div>`,
					disableAutoPan: false,
					pixelOffset: new google.maps.Size(pw / 2, -ph)
			    });

				parentMarker.content.classList.add('gmp-clickable');
		        parentMarker.content.addEventListener('click', () => {
					const isOpen = parentInfoWindow && parentInfoWindow.getMap();
		        	// Close other windows first
		        	infoWindows.forEach((iw) => iw.close());
		        	
					if (parentInfoWindow && !isOpen) {
						parentInfoWindow.open({
							anchor: parentMarker,
							map: googleMap
						});
					} else if (parentInfoWindow && isOpen) {
						parentInfoWindow.close();
					}

		        	// Handle panels and circles if needed for parent click
		        	closeAllMarkerPanels(mapFullID);
		        	dimAllCircles(circles);
					
					if (!isOpen) {
						// Optionally highlight parent panel:
						const parentPanel = document.querySelector(`#${mapFullID} .google-map__infopanel[data-id="parent"]`);
						if (parentPanel) parentPanel.classList.add('selected');
					}
		        });
		    }

			// --- Child Markers Setup ---
	        let childrenMarkers = []; // Array to hold the google.maps.Marker objects
	        let infoWindows = []; // Array for child info windows
	        let polylines = []; // Array for direction polylines
	        let childIndex = 0;
	        const activeMarkerSvg = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_10418_9801)"><path d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z" fill="#FF4100"/><path d="M18.19 11.6001H6V12.6001H18.19V11.6001Z" fill="white"/></g><defs><clipPath id="clip0_10418_9801"><rect width="24" height="24" fill="white"/></clipPath></defs></svg>`;
			const setMarkerActiveState = (marker, isActive) => {
				if (!marker.content) return;
				const activeSvgContainer = marker.content.querySelector('.active-marker-svg');
				if (activeSvgContainer) {
					Array.from(marker.content.children).forEach(child => {
						if (child === activeSvgContainer) {
							child.style.display = isActive ? '' : 'none';
						} else {
							child.style.display = isActive ? 'none' : '';
						}
					});
				}
			};

	        if (mapSettings.markers.length > 0) {
			    mapSettings.markers.forEach((markerConfig) => {
			    	childIndex++;

			    	// --- Prepare Child Marker Icon & Label ---
			    	let childMarkerIconOptions = null;
			    	let childMarkerLabel = null;
					let initialDisplaySize = {}; // To store the calculated display size for later use.
					const specificChildIcon = markerConfig.markerIcon;
					const childIconToUse = specificChildIcon || mapSettings.defaultMarkerIcon || defaultMapMarker;

					const childMarkerEl = document.createElement('div');
					childMarkerEl.className = 'marker child-marker';

					// --- Create Child Marker ---
					const childMarker = new HTMLMapMarker({
						position: markerConfig.position, // Assuming {lat: ..., lng: ...}
						map: googleMap,
						content: childMarkerEl,
					});

					childMarker.markerID = markerConfig.markerID;
					childMarker.pathDisplay = markerConfig.pathDisplay || 'DRIVING';
					childMarker.groupID = markerConfig.groupID || 'group-default';
					childMarker.dialog = markerConfig.dialog || '';
					
			    	if (mapSettings.markerLabels) { // Check if label style is defined
						let labelText = markerConfig.labelText || childIndex.toString();
						if (CONSTANTS.MARKER_LABEL.USE_LEADING_ZERO && parseInt(labelText, 10) < 10) {
							labelText = '0' + labelText;
						}

			    		childMarkerLabel = {
			    			...mapSettings.markerLabels, // Copy style properties (color, fontSize etc.)
			    			text: labelText,
			    			className: "pin-label"
			    		};
			    	}

					// --- SVG Text Injection Logic ---
					if (childIconToUse && childIconToUse.svg && childMarkerLabel && childMarkerLabel.text) {
						let svgString = childIconToUse.svg;
						const textElement = `<text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="${childMarkerLabel.color || '#000'}" font-size="${childMarkerLabel.fontSize || '14px'}" font-family="${childMarkerLabel.fontFamily || 'sans-serif'}" font-weight="${childMarkerLabel.fontWeight || 'normal'}" class="pin-label-svg">${childMarkerLabel.text}</text>`;
						svgString = svgString.replace('</svg>', `${textElement}</svg>`);
						
						initialDisplaySize.width = parseInt(childIconToUse.width);
						initialDisplaySize.height = parseInt(childIconToUse.height);

						childMarkerEl.innerHTML = svgString; // Inject SVG
						const svgEl = childMarkerEl.querySelector('svg');
						if (svgEl) {
							svgEl.style.width = `${initialDisplaySize.width}px`;
							svgEl.style.height = `${initialDisplaySize.height}px`;
						}

						childMarkerLabel = null;
					} else {
						// --- Fallback logic for non-SVG icons, or SVGs without labels ---
						if (childIconToUse && childIconToUse.url && childIconToUse.width && childIconToUse.height) {
							const baseWidth = parseInt(childIconToUse.width);
							const baseHeight = parseInt(childIconToUse.height);

							initialDisplaySize.width = childIconToUse.svg ? baseWidth : baseWidth * CONSTANTS.ICON_SCALE;
							initialDisplaySize.height = childIconToUse.svg ? baseHeight : baseHeight * CONSTANTS.ICON_SCALE;

							const img = document.createElement('img'); // Standard image fallback
							img.src = childIconToUse.url;
							img.width = initialDisplaySize.width;
							img.height = initialDisplaySize.height;
							childMarkerEl.appendChild(img);
						}
						
						if (childMarkerLabel) {
							const labelEl = document.createElement('span');
							labelEl.className = 'pin-label';
							labelEl.textContent = childMarkerLabel.text;
							Object.assign(labelEl.style, {
								color: childMarkerLabel.color,
								fontSize: childMarkerLabel.fontSize,
								fontFamily: childMarkerLabel.fontFamily,
								fontWeight: childMarkerLabel.fontWeight,
							});
							childMarkerEl.appendChild(labelEl);
						}
					}

					// --- Store original data on the marker object for responsive scaling ---
					if (childIconToUse && childIconToUse.svg && !childMarkerLabel) { // We check for !childMarkerLabel because it's nulled out after SVG injection
						childMarker.isSvgIconWithText = true;
						childMarker.originalSvg = childIconToUse.svg;
						let labelText = markerConfig.labelText || childIndex.toString();
						if (CONSTANTS.MARKER_LABEL.USE_LEADING_ZERO && parseInt(labelText, 10) < 10) {
							labelText = '0' + labelText;
						}
						childMarker.originalLabel = { // Store the label info we used
							...mapSettings.markerLabels,
							text: labelText,
						};
					}
					if (childIconToUse && childIconToUse.url && childIconToUse.width && childIconToUse.height) {
						// The 'originalSize' for responsive scaling should be the initial DISPLAYED size.
						childMarker.originalSize = initialDisplaySize;
					}
					if (childMarkerLabel && childMarkerLabel.fontSize) {
						childMarker.originalLabelFontSize = parseFloat(childMarkerLabel.fontSize);
					}
					const activeMarkerContainer = document.createElement('div');
					activeMarkerContainer.className = 'active-marker-svg';
					activeMarkerContainer.style.display = 'none';
					activeMarkerContainer.innerHTML = activeMarkerSvg;
					childMarker.content.appendChild(activeMarkerContainer);

			    	bounds.extend(childMarker.position);
			    	childrenMarkers.push(childMarker);

			    	// --- Child InfoWindow ---
	 				let infoWindow = null;
	 				// Create InfoWindow if dialog type allows, and content exists or directions are enabled
	 				if ((mapSettings.dialogType === 'dialog' || mapSettings.dialogType === 'both') && (childMarker.dialog || mapSettings.directions.enable_directions)) {
						
						// Start with the marker's own dialog content.
						let dialogHtml = childMarker.dialog ? htmlDecode(childMarker.dialog) : '';
						let directionsHtml = '';

						// If directions are enabled, prepare the HTML for it.
						if (mapSettings.directions.enable_directions) {
							// If there's no custom dialog content, provide a default title.
							if (!dialogHtml) {
								dialogHtml = 'Route Details';
							}
							// Create the directions part with placeholders. Add a line break if there's dialog content.
							directionsHtml = `${dialogHtml ? '<br>' : ''}Driving: [driving-distance]km ([driving-time])<br>Walking: [walking-distance]km ([walking-time])`;
						}

						// Only create the InfoWindow if there's some content to show.
						if (dialogHtml) {
							const finalContent = `<div class="dialog-wrap">${dialogHtml}${directionsHtml}</div>`;
							let cw = initialDisplaySize.width || 27;
							let ch = initialDisplaySize.height || 43;
							infoWindow = new google.maps.InfoWindow({
								content: finalContent,
								disableAutoPan: false,
								pixelOffset: new google.maps.Size(cw / 2, -ch)
							});
							infoWindows.push(infoWindow); // Add to array for managing close events
						}
					}

					// --- Directions Logic for Child ---
					if (directionsService && directionsRenderer) {
						// Request Walking Directions
						const walkingRequest = {
							origin: parentMarker.position,
							destination: childMarker.position,
							travelMode: 'WALKING'
						};
						directionsService.route(walkingRequest).then(response => {
							const route = response.routes[0];
							if (route && route.legs[0]) {
						        const leg = route.legs[0];
						        const walkingDistance = (leg.distance.value / 1000).toFixed(2);
						        const walkingTime = leg.duration.text;

						        // Update InfoWindow content if it exists
						        if (infoWindow) {
						        	let currentContent = infoWindow.getContent();
						        	currentContent = currentContent.replace("[walking-distance]", walkingDistance).replace("[walking-time]", walkingTime);
						        	infoWindow.setContent(currentContent);
						        }

						        // If this marker should display the walking path
								if (childMarker.pathDisplay === 'WALKING') {
									const path = google.maps.geometry.encoding.decodePath(route.overview_polyline);
									const polyline = new google.maps.Polyline({
										path: path,
										map: googleMap,
										geodesic: true,
										strokeColor: rgbaToHex(mapSettings.directions.direction_styles?.fill_colour_default || 'rgba(255,0,0,1)').hex, // Default red
										strokeWeight: Number(mapSettings.directions.direction_styles?.stroke_width || 4),
										strokeOpacity: 0, // Start hidden
									});
									polylines.push({ markerID: childMarker.markerID, polyline: polyline, type: 'WALKING' });
								}
							}
						}).catch(e => console.error(`Directions request failed for WALKING to ${childMarker.markerID}: ${e.toString()}`));

						// Request Driving Directions
						const drivingRequest = {
							origin: parentMarker.position,
							destination: childMarker.position,
							travelMode: 'DRIVING'
						};
						directionsService.route(drivingRequest).then(response => {
							const route = response.routes[0];
					    	if (route && route.legs[0]) {
						        const leg = route.legs[0];
						        const drivingDistance = (leg.distance.value / 1000).toFixed(2);
						        const drivingTime = leg.duration.text;

						        // Update InfoWindow content
						        if (infoWindow) {
						        	let currentContent = infoWindow.getContent();
						        	currentContent = currentContent.replace("[driving-distance]", drivingDistance).replace("[driving-time]", drivingTime);
						        	infoWindow.setContent(currentContent);
						        }

						        // If this marker should display the driving path
								if (childMarker.pathDisplay === 'DRIVING') {
									const path = google.maps.geometry.encoding.decodePath(route.overview_polyline);
									const polyline = new google.maps.Polyline({
										path: path,
										map: googleMap,
										strokeColor: rgbaToHex(mapSettings.directions.direction_styles?.fill_colour_default || 'rgba(0,0,255,1)').hex, // Default blue
										strokeWeight: Number(mapSettings.directions.direction_styles?.stroke_width || 4),
										strokeOpacity: 0, // Start hidden
									});
									polylines.push({ markerID: childMarker.markerID, polyline: polyline, type: 'DRIVING' });
								}
							}
						}).catch(e => console.error(`Directions request failed for DRIVING to ${childMarker.markerID}: ${e.toString()}`));
					} // End directions logic

					childMarker.content.classList.add('gmp-clickable');
					childMarker.content.addEventListener('click', () => {
						const isOpen = infoWindow && infoWindow.getMap();

						// Pan to the marker for immediate feedback, especially if no dialog will open.
						googleMap.panTo(childMarker.position);

						// --- Z-Index Management ---
						// Reset z-index for all child markers to their default.
						childrenMarkers.forEach(marker => {
							marker.zIndex = CONSTANTS.Z_INDEX.CHILD_MARKER_DEFAULT;
							setMarkerActiveState(marker, false);
						});

						// Close all other info windows (including parent)
						if (parentInfoWindow) parentInfoWindow.close();
						infoWindows.forEach((iw) => iw.close());

						// Animate out all polylines
						polylines.forEach((p) => animatePolylineOut(p.polyline));

						closeAllMarkerPanels(mapFullID);
						dimAllCircles(circles);

						if (!isOpen) {
							setMarkerActiveState(childMarker, true);
							// Set a high z-index for the clicked marker to bring it to the front.
							childMarker.zIndex = CONSTANTS.Z_INDEX.CHILD_MARKER_ACTIVE;

							// Open this marker's info window (if it exists)
							if (infoWindow) {
								infoWindow.open({
									anchor: childMarker,
									map: googleMap
								});
							}

							// Animate in the correct polyline for this marker
							const activePolyline = polylines.find(p => p.markerID === childMarker.markerID && p.type === childMarker.pathDisplay);
							if (activePolyline) {
								animatePolylineIn(activePolyline.polyline);
							}

							// Handle panels
							if (mapSettings.dialogType === 'drawer' || mapSettings.dialogType === 'both') {
								const panel = document.querySelector(`#${mapFullID} .google-map__infopanel[data-id="${childMarker.markerID}"]`);
								if (panel) panel.classList.add('selected');
							}

							// Handle radial circles
							const latLng = childMarker.position;
							let smallestCircle = null;
							circles.forEach(circle => {
								if (google.maps.geometry.spherical.computeDistanceBetween(latLng, circle.getCenter()) <= circle.getRadius()) {
									if (!smallestCircle || circle.getRadius() < smallestCircle.getRadius()) {
										smallestCircle = circle;
									}
								}
							});
							if (smallestCircle) {
								smallestCircle.setOptions({
									fillColor: rgbaToHex(mapSettings.radial?.radial_styles?.background_colour_active || 'rgba(255,255,0,0.2)').hex, // Default yellow semi-transparent
									fillOpacity: rgbaToHex(mapSettings.radial?.radial_styles?.background_colour_active || 'rgba(255,255,0,0.2)').opacity,
									zIndex: 100 // Bring to front
								});
							}
						}
					});

					// Close listener for info window
					if (infoWindow) {
						infoWindow.addListener('closeclick', () => {
							setMarkerActiveState(childMarker, false);
							// Animate out associated polyline
							const activePolyline = polylines.find(p => p.markerID === childMarker.markerID && p.type === childMarker.pathDisplay);
							if (activePolyline) {
								animatePolylineOut(activePolyline.polyline);
							}
							// Close panel and dim circles
							closeAllMarkerPanels(mapFullID);
							dimAllCircles(circles);
						});
					}

			    }); // End forEach markerConfig
			} // End if mapSettings.markers.length > 0

			// --- Fit Bounds ---
		    if (childrenMarkers.length > 0) { // Only fit if there are child markers
				fitBoundsToVisibleMarkers(googleMap, childrenMarkers.concat(parentMarker), mapSettings.zoom); // Include parent marker in bounds
			} else {
				// If only parent marker, just center and zoom
			    googleMap.setCenter(parentMarker.position);
			    googleMap.setZoom(mapSettings.zoom);
			}

			// --- Radial Circles Setup ---
			let circles = [];
			let labels = [];
			const transparentMarkerIcon = { // Define transparent icon for labels
				url: CONSTANTS.ICONS.TRANSPARENT_1x1,
				size: new google.maps.Size(1, 1),
				origin: new google.maps.Point(0, 0),
				anchor: new google.maps.Point(0, 0)
			};

			if (mapSettings.radial.enable === true && mapSettings.radial.distances && mapSettings.radial.distances.length > 0) {
				if (log) console.log(`Google Maps: Setting up radial circles for ${mapFullID}`);
				const radialStyles = mapSettings.radial.radial_styles || {};
				const labelStyles = mapSettings.radial.label_styles || {};

				// Optional Background Circle (covers area beyond largest radius)
				const bgCircle = new google.maps.Circle({
					strokeOpacity: 0, // No border
					strokeWeight: 0,
					fillColor: rgbaToHex(radialStyles.background_colour_default || 'rgba(0,0,0,0.1)').hex,
					fillOpacity: rgbaToHex(radialStyles.background_colour_default || 'rgba(0,0,0,0.1)').opacity,
					map: googleMap,
					center: parentMarker.position,
					radius: (Number(mapSettings.radial.distances[mapSettings.radial.distances.length - 1]) || 5000) * 1.1, // Slightly larger than largest radius
					zIndex: CONSTANTS.Z_INDEX.RADIAL_BG,
					clickable: false
				});

				// Create circles for each distance
				mapSettings.radial.distances.forEach((distance) => {
					const radius = Number(distance);
					if (isNaN(radius) || radius <= 0) return; // Skip invalid distances

					circles.push(new google.maps.Circle({
						strokeColor: rgbaToHex(radialStyles.boundary_colour_default || 'rgba(255,255,0,0.8)').hex,
						strokeOpacity: rgbaToHex(radialStyles.boundary_colour_default || 'rgba(255,255,0,0.8)').opacity,
						strokeWeight: Number(radialStyles.boundary_stroke_width || 1),
						fillColor: rgbaToHex(radialStyles.background_colour_active || 'rgba(255,255,0,0.1)').hex, // Color when active
						fillOpacity: 0, // Start transparent
						map: googleMap,
						center: parentMarker.position,
						radius: radius,
						zIndex: CONSTANTS.Z_INDEX.RADIAL_CIRCLE, // Above background circle
						clickable: false
					}));

					// Add labels if enabled
					if (labelStyles.enable_labels === true) {
						let circleLabelText = radius + 'm';
						if (radius >= 1000) {
							circleLabelText = (radius / 1000) + 'km';
						}

						const destination = calculateLabelDestination(parentMarker.position.lat(), parentMarker.position.lng(), radius);

						labels.push(new google.maps.Marker({
							position: destination,
							map: googleMap,
							icon: transparentMarkerIcon, // Use the 1x1 transparent icon
							label: {
								text: circleLabelText,
								color: rgbaToHex(labelStyles.label_text_colour || 'rgba(255,255,0,1)').hex,
								// colorOpacity: rgbaToHex(labelStyles.label_text_colour || 'rgba(255,255,0,1)').opacity, // Opacity not directly supported on label color
								fontSize: labelStyles.label_font_size || '10px',
								className: "google-map__radial-label" // For CSS styling
							},
							zIndex: CONSTANTS.Z_INDEX.RADIAL_LABEL // Above circles
						}));
					}
				});
			} // End radial setup

			// --- Map Click Listener (to close things) ---
			googleMap.addListener("click", function(event) {
				if (parentInfoWindow) parentInfoWindow.close();
			    infoWindows.forEach((iw) => iw.close());
				polylines.forEach((p) => animatePolylineOut(p.polyline));
				closeAllMarkerPanels(mapFullID);
				dimAllCircles(circles);
				// Reset z-index of all child markers to default
				childrenMarkers.forEach(marker => {
					marker.setZIndex(CONSTANTS.Z_INDEX.CHILD_MARKER_DEFAULT);
					setMarkerActiveState(marker, false);
				});
			});

			// --- Control Button Listeners ---
			const mapControlsContainer = document.querySelector(`#${mapFullID}`)?.closest('.layout__google_map'); // Find the parent layout container

			if (mapControlsContainer) {
				// Category Controls
				const categoryControls = mapControlsContainer.querySelectorAll('.google-map__controls [data-control-type="categories"] button');
				categoryControls.forEach(button => {
					button.addEventListener("click", () => {
						const groupID = button.dataset.groupId;
						if (log) console.log(`Google Maps (${mapFullID}): Category filter clicked - ${groupID}`);

						// Reset UI - Close everything
						categoryControls.forEach(btn => btn.classList.remove('selected'));
						button.classList.add('selected');
						if (parentInfoWindow) parentInfoWindow.close();
						infoWindows.forEach(iw => iw.close());
						polylines.forEach(p => polylineOut(p.polyline)); // Use instant out for filters
						closeAllMarkerPanels(mapFullID);
						dimAllCircles(circles);

						// Filter markers and apply animation
						childrenMarkers.forEach(child => {
							const isVisible = (groupID === 'all' || child.groupID === groupID);

							if(child.content) {
								child.content.style.display = isVisible ? '' : 'none';
								setMarkerActiveState(child, false);
							}

							// Stop any existing animation from previous clicks
							if (child.content) {
								child.content.classList.remove('bounce');
							}

							if (isVisible) {
								// visibleMarkers logic handled below by filtering again or tracking here
								// Apply bounce animation.
								if (child.content) {
									child.content.classList.add('bounce');
									setTimeout(() => {
										if (child.content) {
											child.content.classList.remove('bounce');
										}
									}, CONSTANTS.ANIMATION.BOUNCE_DURATION);
								}
							}
						});

						const visibleMarkers = childrenMarkers.filter(m => m.content && m.content.style.display !== 'none');
						// Refit bounds if necessary
						if (visibleMarkers.length > 0) {
							fitBoundsToVisibleMarkers(googleMap, visibleMarkers.concat(parentMarker), mapSettings.zoom);
						} else {
							// Optionally zoom to parent if no children match
							googleMap.setCenter(parentMarker.getPosition());
							googleMap.setZoom(mapSettings.zoom);
						}
					});
				});

				// Marker Controls
				const markerControls = mapControlsContainer.querySelectorAll('.google-map__controls [data-control-type="markers"] button');
				markerControls.forEach(button => {
					button.addEventListener("click", () => {
						const markerID = button.dataset.markerId;
						if (log) console.log(`Google Maps (${mapFullID}): Marker control clicked - ${markerID}`);

						// Find the target marker
						const targetMarker = childrenMarkers.find(child => child.markerID === Number(markerID));

						if (targetMarker) {
							// Stop any existing animations on all markers.
							childrenMarkers.forEach(child => {
								if (child.content) {
									child.content.classList.remove('bounce');
								}
							});

							// Apply bounce animation to the target marker.
							if (targetMarker.content) {
								targetMarker.content.classList.add('bounce');
								setTimeout(() => {
									if (targetMarker.content) {
										targetMarker.content.classList.remove('bounce');
									}
								}, CONSTANTS.ANIMATION.BOUNCE_DURATION);
							}

							// Reset UI (except this button)
							markerControls.forEach(btn => btn.classList.remove('selected'));
							button.classList.add('selected');
							// Trigger the marker's click event to handle info window, panel, polyline, etc.
							targetMarker.content.click();

							// --- START: Smooth scroll to map ---
							const lenis = getLenisInstance();
							if (lenis) {
									const header = document.getElementById('header');
									const offset = header ? -header.offsetHeight : 0;
									// mapContainer is the .google-map element, which is a good scroll target.
									lenis.scrollTo(mapContainer, { 
											offset: offset,
											lock: true // Prevent user scroll during programmatic scroll
									});
							}
							// --- END: Smooth scroll to map ---
							// Optionally set zoom: googleMap.setZoom(desiredZoomLevel);
						} else {
							console.warn(`Google Maps (${mapFullID}): Marker control button clicked, but marker ${markerID} not found.`);
						}
					});
				});

				// Radial Controls
				const radialControls = mapControlsContainer.querySelectorAll('.google-map__controls [data-control-type="radial"] button');
				radialControls.forEach(button => {
					button.addEventListener("click", () => {
						const radialID = button.dataset.radialId; // This is the max distance (e.g., '500', '1000', 'all')
						if (log) console.log(`Google Maps (${mapFullID}): Radial filter clicked - ${radialID}`);

						let maxDistance = Infinity;
						if (radialID !== 'all') {
							maxDistance = Number(radialID);
							if (isNaN(maxDistance)) {
								console.warn(`Google Maps (${mapFullID}): Invalid radial distance value: ${radialID}`);
								return;
							}
						}

						// Determine start distance (from previous button or 0)
						let startDistance = 0;
						const prevButton = button.previousElementSibling;
						if (prevButton && prevButton.dataset.radialId && radialID !== 'all') {
							const prevDist = Number(prevButton.dataset.radialId);
							if (!isNaN(prevDist)) {
								startDistance = prevDist;
							}
						}

						// Reset UI
						radialControls.forEach(btn => btn.classList.remove('selected'));
						button.classList.add('selected');
						if (parentInfoWindow) parentInfoWindow.close();
						infoWindows.forEach(iw => iw.close());
						polylines.forEach(p => polylineOut(p.polyline));
						closeAllMarkerPanels(mapFullID);
						dimAllCircles(circles);

						// Filter markers by distance
						const visibleMarkers = [];
						const parentPosition = parentMarker.position;
						childrenMarkers.forEach(child => {
							const distance = google.maps.geometry.spherical.computeDistanceBetween(parentPosition, child.position);
							const isVisible = (radialID === 'all' || (distance <= maxDistance && distance >= startDistance));
							if (child.content) {
								child.content.style.display = isVisible ? '' : 'none';
								setMarkerActiveState(child, false);
							}
							if (isVisible) visibleMarkers.push(child);
						});

						// Refit bounds
						if (visibleMarkers.length > 0) {
							fitBoundsToVisibleMarkers(googleMap, visibleMarkers.concat(parentMarker), mapSettings.zoom);
						} else {
							googleMap.setCenter(parentMarker.position);
							googleMap.setZoom(mapSettings.zoom);
						}
					});
				});

				// --- Combined Category List Header Control (Accordion Toggle + Map Filter) ---
				const categoryListContainer = mapControlsContainer.querySelector('.google-map__columns');
				if (categoryListContainer) {
					categoryListContainer.addEventListener('click', (e) => {
						const catButton = e.target.closest('.google-map__cat');
						if (!catButton) return;

						const parentDiv = catButton.parentElement;
						const olElement = parentDiv.querySelector('ol');
						const firstMarkerButton = olElement ? olElement.querySelector('button[data-marker-id]') : null;
						if (!olElement || !firstMarkerButton) return;

						const wasOpen = parentDiv.classList.contains('mapcat-open');

						// --- 1. Handle Accordion Animation ---
						const allCatDivs = categoryListContainer.querySelectorAll('.google-map__columns > div');
						allCatDivs.forEach(div => {
							const otherOl = div.querySelector('ol');
							if (otherOl && div !== parentDiv) {
								div.classList.remove('mapcat-open');
								otherOl.style.height = '0px';
								otherOl.style.opacity = '0';
								otherOl.style.marginTop = '0';
							}
						});

						if (!wasOpen) {
							parentDiv.classList.add('mapcat-open');
							olElement.style.height = olElement.scrollHeight + 'px';
							olElement.style.opacity = '1';
							olElement.style.marginTop = '1em';
							setTimeout(() => window.dispatchEvent(new Event('resize')), 300);
						} else {
							parentDiv.classList.remove('mapcat-open');
							olElement.style.height = '0px';
							olElement.style.opacity = '0';
							olElement.style.marginTop = '0';
							setTimeout(() => window.dispatchEvent(new Event('resize')), 300);
						}

						// --- 2. Handle Map Filtering ---
						// If the category was already open, clicking it again should show all markers.
						if (wasOpen) {
							if (log) console.log(`Google Maps (${mapFullID}): Closing category. Showing all markers.`);
							showAllMarkers(childrenMarkers, parentMarker, googleMap, mapSettings.zoom);
							resetMapUI(parentInfoWindow, infoWindows, polylines, mapFullID);
							return; // Stop further execution
						}

						// --- If it was closed, proceed with filtering ---
						const firstMarkerInCatId = Number(firstMarkerButton.dataset.markerId);
						const representativeMarker = childrenMarkers.find(m => m.markerID === firstMarkerInCatId);
						if (!representativeMarker) {
							if (log) console.error(`Google Maps (${mapFullID}): Could not find representative marker with ID ${firstMarkerInCatId}.`);
							return;
						}

						const categoryIDToFilter = representativeMarker.groupID;
						if (log) console.log(`Google Maps (${mapFullID}): Category header clicked. Filtering for groupID: ${categoryIDToFilter}`);

						// Reset UI elements
						resetMapUI(parentInfoWindow, infoWindows, polylines, mapFullID);

						// Filter markers and apply animation
						const visibleMarkers = filterAndAnimateMarkers(childrenMarkers, categoryIDToFilter);

						// Refit bounds to the visible markers in the category
						if (visibleMarkers.length > 0) {
							fitBoundsToVisibleMarkers(googleMap, visibleMarkers.concat(parentMarker), mapSettings.zoom);
						} else {
							// Fallback in case no markers are found
							googleMap.setCenter(parentMarker.position);
						}
					});

					// --- Accordion Height Recalculation on Resize ---
					// Debounce the resize handler for performance.
					const handleResize = debounce(() => {
						// Find the currently open accordion panel, if any.
						const openAccordion = categoryListContainer.querySelector('.mapcat-open');
						if (openAccordion) {
							const olElement = openAccordion.querySelector('ol');
							if (olElement) {
								// Recalculate and set the height based on the new scrollHeight.
								olElement.style.height = olElement.scrollHeight + 'px';
							}
						}
					}, 150);
					window.addEventListener('resize', handleResize);
				}
			} else if (log) {
				console.log(`Google Maps (${mapFullID}): Controls container not found within .layout__google_map.`);
			}

			// --- Responsive Marker Resizing ---
			const allMarkers = [parentMarker, ...childrenMarkers];
			const responsiveMarkerConfig = globalSettings.responsiveMarkerConfig;

			// Only set up resizing if there are markers and a valid config is provided.
			if (allMarkers.length > 0 && responsiveMarkerConfig && responsiveMarkerConfig.breakpoints && responsiveMarkerConfig.scales) {
				// Create a debounced version of the resizing function for efficiency.
				const debouncedUpdate = debounce(() => updateMarkerSizes(allMarkers, responsiveMarkerConfig), 250);
				window.addEventListener('resize', debouncedUpdate);
				
				// Initial call to set the correct size on load.
				updateMarkerSizes(allMarkers, responsiveMarkerConfig);
			} else if (log && allMarkers.length > 0 && !responsiveMarkerConfig) {
				console.warn(`Google Maps (${mapFullID}): Responsive marker config not found or invalid in globalSettings. Resizing disabled.`);
			}
		} // End for loop
	} catch (e) {
		console.error("Google Maps: Failed to load API.", e);
	}

	if (log) console.log('googleMaps component initialization requested.');
} // End export function googleMaps


// --- Helper Functions (Keep these outside the main exported function but within the module scope) ---

/**
 * Resets common UI elements on the map.
 */
function resetMapUI(parentInfoWindow, infoWindows, polylines, mapFullID) {
	if (parentInfoWindow) parentInfoWindow.close();
	infoWindows.forEach(iw => iw.close());
	polylines.forEach(p => polylineOut(p.polyline));
	closeAllMarkerPanels(mapFullID);
}

/**
 * Makes all child markers visible and fits the map bounds to them.
 */
function showAllMarkers(childrenMarkers, parentMarker, googleMap, defaultZoom) {
	childrenMarkers.forEach(child => {
		if (child.content) {
			child.content.classList.remove('bounce'); // Stop any bounce animation
			child.content.style.display = ''; // Make marker visible
		}
	});
	// Fit bounds to all markers
	fitBoundsToVisibleMarkers(googleMap, childrenMarkers.concat(parentMarker), defaultZoom);
}

/**
 * Filters markers by a group ID, makes them visible, and applies an animation.
 * @param {Array} childrenMarkers - The array of all child marker objects.
 * @param {string|number} groupIDToFilter - The ID of the group to show.
 * @returns {Array} An array of the markers that were made visible.
 */
function filterAndAnimateMarkers(childrenMarkers, groupIDToFilter) {
	let visibleMarkers = [];
	childrenMarkers.forEach(child => {
		const isVisible = child.groupID === groupIDToFilter;
		
		if (child.content) {
			child.content.style.display = isVisible ? '' : 'none';
			child.content.classList.remove('bounce');
		}

		if (isVisible) {
			visibleMarkers.push(child);
			if (child.content) {
				child.content.classList.add('bounce');
				setTimeout(() => {
					if (child.content) {
						child.content.classList.remove('bounce');
					}
				}, CONSTANTS.ANIMATION.BOUNCE_DURATION);
			}
		}
	});
	return visibleMarkers;
}


function fitBoundsToVisibleMarkers(googleMap, googleMarkers, defaultZoom) {
    let bounds = new google.maps.LatLngBounds();
    let visibleCount = 0;

    googleMarkers.forEach(marker => {
		if (!marker.content || marker.content.style.display !== 'none') {
            bounds.extend(marker.position);
            visibleCount++;
        }
    });

    if (visibleCount > 0) {
        googleMap.fitBounds(bounds);

        // Adjust zoom if only one marker is visible or bounds are very small
        if (visibleCount === 1) {
            // If only one marker, fitBounds might zoom in too much. Set a default zoom.
             let listener = google.maps.event.addListenerOnce(googleMap, "idle", function() {
                googleMap.setZoom(defaultZoom || 14); // Use passed default or fallback
            });
        } else {
             // For multiple markers, listen for the map to be idle after fitting bounds,
             // then adjust the zoom to be tighter.
             let listener = google.maps.event.addListenerOnce(googleMap, "idle", function() {
                let currentZoom = googleMap.getZoom();
                // Increase the zoom by 1 to make it tighter, but don't exceed a max zoom level.
                googleMap.setZoom(Math.min(currentZoom, CONSTANTS.MAP.MAX_FIT_BOUNDS_ZOOM));
            });
        }
    }
    // If visibleCount is 0, the map remains unchanged (or you could center on parent)
}

function closeAllMarkerPanels(mapFullID) {
	// Use the full mapFullID passed which includes 'google-map--' prefix
	document.querySelectorAll(`#${mapFullID} .google-map__infopanel`).forEach(function(markerPanel) {
		markerPanel.classList.remove('selected');
	});
}

function dimAllCircles(circles) {
	circles.forEach(circle => {
		circle.setOptions({
			fillOpacity: 0, // Make fill transparent
			zIndex: CONSTANTS.Z_INDEX.RADIAL_CIRCLE // Reset zIndex
		});
	});
}

function animatePolylineIn(polyline) {
	if (!polyline) return;
	let opacity = 0;
	// Clear previous timer if any (important for rapid clicks)
	if (polyline.animationTimerIn) clearInterval(polyline.animationTimerIn);
	if (polyline.animationTimerOut) clearInterval(polyline.animationTimerOut);

	polyline.animationTimerIn = setInterval(() => {
		opacity += 0.1;
		if (opacity >= 1) {
			clearInterval(polyline.animationTimerIn);
			polyline.setOptions({ strokeOpacity: 1 });
			delete polyline.animationTimerIn; // Clean up timer reference
		} else {
			polyline.setOptions({ strokeOpacity: opacity });
		}
	}, CONSTANTS.ANIMATION.POLYLINE_INTERVAL);
}

function animatePolylineOut(polyline) {
	if (!polyline || polyline.get('strokeOpacity') === 0) return; // Don't animate if already out
	let opacity = polyline.get('strokeOpacity') || 1; // Start from current opacity
	// Clear previous timer if any
	if (polyline.animationTimerIn) clearInterval(polyline.animationTimerIn);
	if (polyline.animationTimerOut) clearInterval(polyline.animationTimerOut);

	polyline.animationTimerOut = setInterval(() => {
		opacity -= 0.1;
		if (opacity <= 0) {
			clearInterval(polyline.animationTimerOut);
			polyline.setOptions({ strokeOpacity: 0 });
			delete polyline.animationTimerOut; // Clean up timer reference
		} else {
			polyline.setOptions({ strokeOpacity: opacity });
		}
	}, CONSTANTS.ANIMATION.POLYLINE_INTERVAL);
}

// Instantly hide polyline (used for filters)
function polylineOut(polyline) {
	if (!polyline) return;
	// Clear any running animations
	if (polyline.animationTimerIn) clearInterval(polyline.animationTimerIn);
	if (polyline.animationTimerOut) clearInterval(polyline.animationTimerOut);
	delete polyline.animationTimerIn;
	delete polyline.animationTimerOut;
	// Set opacity directly
	polyline.setOptions({ strokeOpacity: 0 });
}

function calculateLabelDestination(lat1, lon1, radialDistance) {
	// Simple approach: place label slightly North-East of the circle edge
	const EarthRadius = 6371e3; // meters
	const bearing = 45 * Math.PI / 180; // 45 degrees (North-East) in radians
	const lat1Rad = lat1 * Math.PI / 180;
	const lon1Rad = lon1 * Math.PI / 180;
	const angularDistance = radialDistance / EarthRadius;

	const lat2Rad = Math.asin(Math.sin(lat1Rad) * Math.cos(angularDistance) +
	                         Math.cos(lat1Rad) * Math.sin(angularDistance) * Math.cos(bearing));

	const lon2Rad = lon1Rad + Math.atan2(Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(lat1Rad),
	                                   Math.cos(angularDistance) - Math.sin(lat1Rad) * Math.sin(lat2Rad));

	return {
		lat: lat2Rad * 180 / Math.PI,
		lng: lon2Rad * 180 / Math.PI
	};
}

function rgbaToHex(rgbaString = '') {
	const defaultValue = { hex: '#000000', opacity: 0 }; // Default fallback
	if (typeof rgbaString !== 'string') return defaultValue;

	const match = rgbaString.match(/^rgba?\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3})(?:,\s*(\d?\.?\d+))?\)$/);

	if (!match) return defaultValue; // Return default if regex fails

	const r = parseInt(match[1], 10);
	const g = parseInt(match[2], 10);
	const b = parseInt(match[3], 10);
	const a = match[4] !== undefined ? parseFloat(match[4]) : 1; // Default alpha to 1 if not present

	// Clamp values
	const clamp = (num, min, max) => Math.min(Math.max(num, min), max);
	const clampedR = clamp(r, 0, 255);
	const clampedG = clamp(g, 0, 255);
	const clampedB = clamp(b, 0, 255);
	const clampedA = clamp(a, 0, 1);

	const rHex = clampedR.toString(16).padStart(2, '0');
	const gHex = clampedG.toString(16).padStart(2, '0');
	const bHex = clampedB.toString(16).padStart(2, '0');

	return {
		hex: `#${rHex}${gHex}${bHex}`,
		opacity: clampedA
	};
}

function htmlDecode(input) {
	if (typeof input !== 'string') return '';
    const doc = new DOMParser().parseFromString(input, "text/html");
    // Use textContent which automatically handles decoding
    return doc.documentElement.textContent || '';
}

/**
 * Resizes all map markers based on the current viewport width and responsive configuration.
 * This function is designed to be called on window resize (debounced).
 * @param {Array<google.maps.Marker>} allMarkers - An array of all marker objects to resize.
 * @param {object} responsiveMarkerConfig - The configuration object with breakpoints and scales.
 */
function updateMarkerSizes(allMarkers, responsiveMarkerConfig) {
	const screenWidth = window.innerWidth;
	let sizeTier = 'large';
	if (screenWidth <= responsiveMarkerConfig.breakpoints.md) {
		sizeTier = 'medium';
	}
	if (screenWidth <= responsiveMarkerConfig.breakpoints.xs) {
		sizeTier = 'small';
	}

	const scale = responsiveMarkerConfig.scales[sizeTier];

	allMarkers.forEach(marker => {
		if (!marker.content || !marker.originalSize) return;

		const newWidth = Math.round(marker.originalSize.width * scale);
		const newHeight = Math.round(marker.originalSize.height * scale);

		const elementToResize = marker.content.querySelector('img') || marker.content.querySelector('svg');
		if (elementToResize) {
			elementToResize.style.width = `${newWidth}px`;
			elementToResize.style.height = `${newHeight}px`;
		}

		if (marker.isSvgIconWithText === true) {
			const labelInfo = marker.originalLabel;
			const originalFontSize = parseFloat(labelInfo.fontSize || '14px');
			const newFontSize = Math.round(originalFontSize * scale);
			const textElement = marker.content.querySelector('.pin-label-svg');
			if (textElement) {
				textElement.style.fontSize = `${newFontSize}px`;
			}
		} else {
			const label = marker.content.querySelector('.pin-label');
			if (label && marker.originalLabelFontSize) {
				const newFontSize = Math.round(marker.originalLabelFontSize * scale);
				label.style.fontSize = `${newFontSize}px`;
			}
		}
	});
}
